import { useState, type FormEvent } from 'react';
import { Button, Dialog, Input, Label, Select, Textarea } from '../components/ui';
import { useBuilders, useCommunities, useCrews } from '../lib/queries';
import { useDeleteJob, useUpdateJob, useUpsertJob } from '../lib/mutations';
import type { JobWithContext } from '../lib/database.types';

interface EditJobDialogProps {
    open: boolean;
    onClose: () => void;
    job: JobWithContext | null;
}

export function EditJobDialog({ open, onClose, job }: EditJobDialogProps) {
    const isEdit = Boolean(job);
    const { data: builders = [] } = useBuilders();
    const { data: communities = [] } = useCommunities();
    const { data: crews = [] } = useCrews();
    const upsertMut = useUpsertJob();
    const updateMut = useUpdateJob();
    const deleteMut = useDeleteJob();

    const [builderId, setBuilderId] = useState(job?.builder_id ?? '');
    const [communityText, setCommunityText] = useState(job?.community.name ?? '');
    const [lotNumber, setLotNumber] = useState(job?.lot_number ?? '');
    const [address, setAddress] = useState(job?.address ?? '');
    const [contactName, setContactName] = useState(job?.contact_name ?? '');
    const [contactPhone, setContactPhone] = useState(job?.contact_phone ?? '');
    const [crewId, setCrewId] = useState(job?.crew_id ?? '');
    const [notes, setNotes] = useState(job?.notes ?? '');
    const [error, setError] = useState<string | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const builderName = builders.find((b) => b.id === builderId)?.name ?? '';
    const lotChanged = isEdit && job ? lotNumber.trim() !== job.lot_number : false;

    // Scope community suggestions to the selected builder
    const communitySuggestions = communities
        .filter((c) => c.builder_id === builderId)
        .map((c) => c.name);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setError(null);

        if (!builderName || !communityText.trim() || !lotNumber.trim()) {
            setError('Builder, community, and lot number are all required.');
            return;
        }

        try {
            if (isEdit && job) {
                // Builder and community stay fixed. They anchor the JobTread link
                // and the community is a foreign key, so changing either here
                // would orphan the row rather than move it.
                await updateMut.mutateAsync({
                    id: job.id,
                    patch: {
                        lot_number: lotNumber.trim(),
                        address: address.trim() || null,
                        contact_name: contactName.trim() || null,
                        contact_phone: contactPhone.trim() || null,
                        crew_id: crewId || null,
                        notes: notes.trim() || null,
                    },
                });
            } else {
                const created = await upsertMut.mutateAsync({
                    p_builder_name: builderName,
                    p_community_name: communityText.trim(),
                    p_lot_number: lotNumber.trim(),
                    p_address: address.trim() || null,
                    p_contact_name: contactName.trim() || null,
                    p_contact_phone: contactPhone.trim() || null,
                    p_notes: notes.trim() || null,
                });
                // upsert_job has no crew parameter, so a crew picked on a new job
                // is applied as a follow-up patch rather than silently dropped.
                if (crewId && created?.id) {
                    await updateMut.mutateAsync({ id: created.id, patch: { crew_id: crewId } });
                }
            }
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        }
    }

    async function handleBoardPlacement() {
        if (!job) return;
        setError(null);
        try {
            await updateMut.mutateAsync({
                id: job.id,
                patch: { force_active: !job.force_active },
            });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not move the job');
        }
    }

    async function handleDelete() {
        if (!job) return;
        setError(null);
        try {
            await deleteMut.mutateAsync(job.id);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delete failed');
            setConfirmingDelete(false);
        }
    }

    const busy = upsertMut.isPending || updateMut.isPending || deleteMut.isPending;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? `Edit ${job?.community.name} · Lot ${job?.lot_number}` : 'New job'}
            subtitle={isEdit ? 'Builder and community are fixed after creation. The lot number can be edited.' : undefined}
            maxWidth="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="job-form"
                        disabled={busy}
                        onClick={handleSubmit}
                    >
                        {busy ? 'Saving...' : isEdit ? 'Save' : 'Create job'}
                    </Button>
                </>
            }
        >
            <form id="job-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="builder">Builder</Label>
                        <Select
                            id="builder"
                            value={builderId}
                            onChange={(e) => setBuilderId(e.target.value)}
                            disabled={isEdit}
                            required
                        >
                            <option value="">— select —</option>
                            {builders.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="lot">Lot number</Label>
                        <Input
                            id="lot"
                            value={lotNumber}
                            onChange={(e) => setLotNumber(e.target.value)}
                            required
                            placeholder="88 or 62 (Bldg 11)"
                        />
                        <p className="font-mono text-[10px] text-graphite-500 mt-1">
                            Add or remove markers like "New Start" or "HOLD" here.
                        </p>
                    </div>
                </div>

                {/* Renaming a lot is safe for the board and risky for the PO feed,
                    so say so at the moment it matters rather than in a manual. */}
                {lotChanged ? (
                    <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        Incoming POs match a job on its exact lot number, so once this is saved as
                        "{lotNumber.trim()}" the next PO for lot {job?.lot_number} will not find it and
                        will create a second row instead. Rename freely for jobs you are tracking by
                        hand. For a lot still receiving POs, expect a duplicate to appear and tell
                        Devon so the pair can be merged.
                    </div>
                ) : null}

                <div>
                    <Label htmlFor="community">Community</Label>
                    <Input
                        id="community"
                        value={communityText}
                        onChange={(e) => setCommunityText(e.target.value)}
                        disabled={isEdit}
                        required
                        list={isEdit ? undefined : 'community-suggestions'}
                        placeholder="DR Zion Springs"
                    />
                    {!isEdit && communitySuggestions.length ? (
                        <datalist id="community-suggestions">
                            {communitySuggestions.map((name) => (
                                <option key={name} value={name} />
                            ))}
                        </datalist>
                    ) : null}
                    <p className="font-mono text-[10px] text-graphite-500 mt-1">
                        Include the builder prefix as Andrea writes it: "DR Zion Springs", "Red Cedar Sugar Creek".
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="address">Address</Label>
                        <Input
                            id="address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="123 Main St"
                        />
                    </div>
                    <div>
                        {/* Job level crew. The schedule dialog still sets a crew per
                            stage; this one is what the board column, its filter and
                            the export read, which is how each crew gets its list. */}
                        <Label htmlFor="job-crew">Crew</Label>
                        <Select
                            id="job-crew"
                            value={crewId}
                            onChange={(e) => setCrewId(e.target.value)}
                        >
                            <option value="">— none —</option>
                            {crews.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Select>
                        <p className="font-mono text-[10px] text-graphite-500 mt-1">
                            Crew for the whole job. Filter the board by this and export the slice.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="contact-name">Superintendent</Label>
                        <Input
                            id="contact-name"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            placeholder="Dylan"
                        />
                    </div>
                    <div>
                        <Label htmlFor="contact-phone">Superintendent phone</Label>
                        <Input
                            id="contact-phone"
                            type="tel"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="(704) 555-0123"
                        />
                    </div>
                </div>

                <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                        id="notes"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Pipe bollards, brick schedule, etc."
                    />
                </div>

                {error ? (
                    <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        {error}
                    </div>
                ) : null}
            </form>

            {isEdit && job ? (
                <div className="mt-5 pt-4 border-t border-graphite-200 dark:border-graphite-800 space-y-4">
                    {/* Board placement. Pins the job on the active board without
                        touching stage history, so undoing a mistaken completion
                        never rewrites a pour date the weather packet depends on. */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400">
                                Board placement
                            </p>
                            <p className="text-xs text-graphite-500 mt-1">
                                {job.force_active
                                    ? 'This job is being held on the active board by hand. Releasing it lets it move to Completed once the slab and flatwork are done.'
                                    : 'Pull this job back onto the active board if it was marked complete by mistake. Its dates are left exactly as they are.'}
                            </p>
                        </div>
                        <Button variant="secondary" onClick={handleBoardPlacement} disabled={busy} className="shrink-0">
                            {job.force_active ? 'Release to Completed' : 'Keep on board'}
                        </Button>
                    </div>

                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-600 dark:text-amber-400">
                                Delete
                            </p>
                            <p className="text-xs text-graphite-500 mt-1">
                                {confirmingDelete
                                    ? `Permanently deletes ${job.community.name} lot ${job.lot_number}, its schedule, and any weather packet data pulled for it. This cannot be undone. Only do this if no dispute packet for this lot has gone to a builder.`
                                    : 'Removes the job and its schedule for good. Use this for duplicates and lots that are not real jobs.'}
                            </p>
                        </div>
                        {confirmingDelete ? (
                            <div className="flex gap-2 shrink-0">
                                <Button variant="ghost" onClick={() => setConfirmingDelete(false)} disabled={busy}>
                                    Cancel
                                </Button>
                                <Button variant="danger" onClick={handleDelete} disabled={busy}>
                                    {deleteMut.isPending ? 'Deleting...' : 'Delete for good'}
                                </Button>
                            </div>
                        ) : (
                            <Button variant="danger" onClick={() => setConfirmingDelete(true)} disabled={busy} className="shrink-0">
                                Delete job
                            </Button>
                        )}
                    </div>
                </div>
            ) : null}
        </Dialog>
    );
}
